import React, { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { Modal } from 'react-bootstrap';
import { toast } from 'react-toastify';
import Grid from '@mui/material/Grid2';
import { apiService } from '../services/apiService';
import { useLocation } from 'react-router-dom';

function SessionActive() {
    const location = useLocation();
    const currentPath = location.pathname;
    const [btnLoading, setBtnLoading] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [countdown, setCountdown] = useState(300); // 5 minutes = 300 seconds
    const [isCountdownExpired, setIsCountdownExpired] = useState(false);
    const [backgroundTimer, setBackgroundTimer] = useState(0); // Background timer in seconds
    
    const userId = localStorage.getItem('userid');
    const inactivityTimer = useRef(null);
    const countdownTimer = useRef(null);
    const backgroundTimerRef = useRef(null);


    // Handle logout functionality
    const handleLogout = useCallback(async () => {
        try {
            await apiService.postRequest('/auth/logout');
        } catch (error) {
            console.error("Logout API failed:", error);
        } finally {
            // Clear all timers before logout
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            if (countdownTimer.current) clearInterval(countdownTimer.current);
            if (backgroundTimerRef.current) clearInterval(backgroundTimerRef.current);
            
            localStorage.clear();
            sessionStorage.clear();
            if(currentPath !== "/"){
                window.location.href = "/login";
            }
        }
    }, [currentPath]);
    
    useEffect(() => {
        const expiry = localStorage.getItem("sessionExpiry");
        const warning = localStorage.getItem("sessionWarning");

        if (!expiry || !warning || !userId) return;

        const expiryTime = parseInt(expiry, 10);
        const warningMinutes = parseInt(warning, 10);
        const now = Date.now();
        const remainingTime = expiryTime - now;
        
        // If session expired, handle logout
        if (remainingTime <= 0) {
            console.log("Session expired, should logout");
            handleLogout();
            // Handle logout logic here
            return;
        }
        
        // Calculate warning trigger time
        const warningTime = remainingTime - (warningMinutes * 60 * 1000);
        console.log("Session expires in:", Math.round(remainingTime / 1000), "seconds");
        console.log("Warning time:", Math.round(warningTime / 1000), "seconds");
        // Only set timer if warning time is in the future (more than 5 seconds)
        if (warningTime > 5000) {
            console.log("Setting warning timer for:", Math.round(warningTime / 1000), "seconds");
            
            const warningTimer = setTimeout(async () => {
                console.log("WARNING TIME REACHED - Session expires in", warningMinutes, "minutes");
                
                // Auto-extend only if popup is not showing
                if (!showPopup) {
                    try {
                        const response = await apiService.postRequest('extend-session', {
                            user_id: userId,
                        });
                        
                        if (response?.success) {
                            const { session_timeout, session_warning } = response;
                            if (session_timeout) {
                                const newExpiryTime = Date.now() + session_timeout * 60 * 1000;
                                localStorage.setItem("sessionExpiry", newExpiryTime.toString());
                            }
                            if (session_warning) {
                                localStorage.setItem("sessionWarning", session_warning.toString());
                            }
                        }
                    } catch (error) {
                        console.error("Auto-extension failed:", error);
                    }
                }
            }, warningTime);
            
            return () => clearTimeout(warningTimer);
            
        }else if (warningTime < 0) {
            console.log("Warning time is very soon, extending immediately");
            if (!showPopup) {
                apiService.postRequest('extend-session', { user_id: userId })
                    .then(response => {
                        if (response?.success) {
                            const { session_timeout } = response;
                            if (session_timeout) {
                                const newExpiryTime = Date.now() + session_timeout * 60 * 1000;
                                localStorage.setItem("sessionExpiry", newExpiryTime.toString());
                            }
                        }
                    })
                    .catch(error => console.error("Immediate extension failed:", error));
            }
        } else if (warningTime > 0) {
            if (!showPopup) {
                apiService.postRequest('extend-session', { user_id: userId })
                .then(response => {
                    if (response?.success) {
                        const { session_timeout } = response;
                        if (session_timeout) {
                            const newExpiryTime = Date.now() + session_timeout * 60 * 1000;
                            localStorage.setItem("sessionExpiry", newExpiryTime.toString());
                        }
                    }
                })
                .catch(error => console.error("Immediate extension failed:", error));
            }
        } else {
            // Warning time has passed, should show popup or extend
            console.log("Warning time has already passed");
        }
        
    }, [showPopup, userId, handleLogout]);
    // Configuration constants - using useMemo to prevent recreation on every render
    
    const { INACTIVITY_TIME, POPUP_COUNTDOWN_TIME, TOTAL_SESSION_TIME } = useMemo(() => ({
        INACTIVITY_TIME: 15 * 60, // 1 minute in seconds
        POPUP_COUNTDOWN_TIME: 5 * 60, // 5 minutes in seconds
        TOTAL_SESSION_TIME: (15 * 60) + (5 * 60) // 6 minutes total
    }), []);

    const lastActivityTimeRef = useRef(Date.now());

    // Handle exit button click
    const handleClose = () => {
        handleLogout();
    };

    const handleLogoutrefresh = useCallback(async () => {
        try {
            await apiService.postRequest('/auth/logout');
            console.log("Test logout")
        } catch (error) {
            console.error("Logout API failed:", error);
        } finally {
            // Clear all timers before logout
            if (inactivityTimer.current) clearTimeout(inactivityTimer.current);
            if (countdownTimer.current) clearInterval(countdownTimer.current);
            if (backgroundTimerRef.current) clearInterval(backgroundTimerRef.current);
            
            localStorage.clear();
            sessionStorage.clear();
        }
    }, []);

    // Handle session timeout
    const handleTimeout = useCallback(() => {
        setIsCountdownExpired(true);
    }, []);
    
    useEffect(() => {
        if (isCountdownExpired) {
            console.log("Test logout1")
            handleLogoutrefresh();
        }
    }, [isCountdownExpired, handleLogoutrefresh]);

    const startCountdown = useCallback(() => {
        setCountdown(POPUP_COUNTDOWN_TIME);
        setIsCountdownExpired(false);
        
        if (countdownTimer.current) {
            clearInterval(countdownTimer.current);
        }
        
        countdownTimer.current = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(countdownTimer.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [POPUP_COUNTDOWN_TIME]);
    // Start the background timer that runs continuously
   const startBackgroundTimer = useCallback(() => {
  if (backgroundTimerRef.current) {
    clearInterval(backgroundTimerRef.current);
  }

  backgroundTimerRef.current = setInterval(() => {
    const now = Date.now();
    const elapsed = Math.floor((now - lastActivityTimeRef.current) / 1000);

    // Show popup after inactivity threshold
    if (elapsed >= INACTIVITY_TIME && !showPopup) {
      setShowPopup(true);
      startCountdown();
    }

    // End session after total session time
    if (elapsed >= TOTAL_SESSION_TIME) {
      clearInterval(backgroundTimerRef.current);
      handleTimeout();
    }

    setBackgroundTimer(elapsed);
  }, 1000);
}, [INACTIVITY_TIME, TOTAL_SESSION_TIME, showPopup, startCountdown, handleTimeout]);

    // Reset when activity detected
    const resetAllTimers = useCallback(() => {
    if (!showPopup) {
        const now = Date.now();
        lastActivityTimeRef.current = now;
        
        localStorage.setItem("lastActivity", now.toString());

        if (countdownTimer.current) clearInterval(countdownTimer.current);
        if (backgroundTimerRef.current) clearInterval(backgroundTimerRef.current);

        setCountdown(POPUP_COUNTDOWN_TIME);
        setIsCountdownExpired(false);
        setBackgroundTimer(0);

        startBackgroundTimer();
    }
    }, [POPUP_COUNTDOWN_TIME, showPopup, startBackgroundTimer]);

    useEffect(() => {
        const syncActivity = (e) => {
            if (e.key === "lastActivity" && e.newValue) {
            lastActivityTimeRef.current = parseInt(e.newValue, 10);
            resetAllTimers(); // ⬅ reset timers if other tab has activity
            }

            if (e.key === "sessionExtended" && e.newValue) {
            // Another tab extended session → reset here too
                lastActivityTimeRef.current = Date.now();
                setShowPopup(false);
                resetAllTimers();
            }
        };

        window.addEventListener("storage", syncActivity);
        return () => window.removeEventListener("storage", syncActivity);
    }, [resetAllTimers]);
    
    // Setup event listeners for user activity
    useEffect(() => {
        const events = ["mousemove", "keydown", "scroll", "click", "touchstart"];
        
        // Store refs in variables to avoid stale closure issues
        const inactivityTimerRef = inactivityTimer.current;
        const countdownTimerRef = countdownTimer.current;
        const backgroundTimerRefCurrent = backgroundTimerRef.current;
        
        events.forEach((event) => {
            window.addEventListener(event, resetAllTimers, { passive: true });
        });

        // Start the background timer when component mounts
        startBackgroundTimer();

        // Cleanup on unmount
        return () => {
            events.forEach((event) => {
                window.removeEventListener(event, resetAllTimers);
            });
            if (inactivityTimerRef) clearTimeout(inactivityTimerRef);
            if (countdownTimerRef) clearInterval(countdownTimerRef);
            if (backgroundTimerRefCurrent) clearInterval(backgroundTimerRefCurrent);
        };
    }, [resetAllTimers, startBackgroundTimer]);

    // Format time display (MM:SS)
    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, "0");
        const s = (seconds % 60).toString().padStart(2, "0");
        return `${m}:${s}`;
    };

    // Format background timer for debugging (optional)
    const formatBackgroundTime = (backgroundTimer) => {
        const totalMinutes = Math.floor(backgroundTimer / 60);
        const remainingSeconds = backgroundTimer % 60;
        return `${totalMinutes}:${remainingSeconds.toString().padStart(2, "0")}`;
    };

    // Extend session functionality
    const extendSession = async () => {
        setBtnLoading(true);
        try {
            const response = await apiService.postRequest('extend-session', {
                user_id: userId,
            });
            
            if (response && response.success === true) {
                // Update session data if provided
                const { session_timeout, session_warning } = response;
                if (session_timeout) {
                    const expiryTime = Date.now() + session_timeout * 60 * 1000;
                    localStorage.setItem("sessionExpiry", expiryTime.toString());
                }
                if (session_warning) {
                    localStorage.setItem("sessionWarning", session_warning.toString());
                }
                
                lastActivityTimeRef.current = Date.now();
                // Reset all timers and hide popup

                localStorage.setItem("sessionExtended", Date.now().toString());

                setShowPopup(false);
                resetAllTimers();
                setCountdown(POPUP_COUNTDOWN_TIME);
                setBackgroundTimer(0);
                startBackgroundTimer();
                toast.success("Session extended successfully!");
            } else {
                toast.error(response.message || "Session Expired!");
                if (response && response.authenticate === false) {
                    handleLogout();
                }
            }
        } catch (error) {
            console.error("Error extending session:", error);
            toast.error("Failed to extend session");
        } finally {
            setBtnLoading(false);
        }
    };

    useEffect(() => {
        const handleBeforeUnload = () => {
        if (isCountdownExpired) {
            // Save a flag so after reload we know to log out
            localStorage.setItem("forceLogoutOnReload", "true");
        }
        };

        window.addEventListener("beforeunload", handleBeforeUnload);

        return () => {
        window.removeEventListener("beforeunload", handleBeforeUnload);
        };
    }, [isCountdownExpired]);

    useEffect(() => {
        // When the page reloads, check the flag and log out immediately
        if (localStorage.getItem("forceLogoutOnReload") === "true") {
        localStorage.removeItem("forceLogoutOnReload");
        handleLogout();
        }
    }, [handleLogout]);
    return (
        <>
            {/* Debug info - remove in production */}
            <div style={{ 
                position: 'fixed', 
                top: '10px', 
                left: '10px', 
                backgroundColor: 'rgba(0,0,0,0.8)', 
                color: 'white',  
                padding: '10px', 
                fontSize: '12px',
                zIndex: 9999,
                borderRadius: '5px',
                display:'none'
            }}>
                Background Timer: {formatBackgroundTime(backgroundTimer)} / {formatBackgroundTime(TOTAL_SESSION_TIME)}
                <br />
                Status: {showPopup ? 'Popup Shown' : 'Normal'}
                <br />
                Countdown: {formatTime(countdown)}
            </div>

            <Modal
                show={showPopup} 
                onHide={() => {}} // Prevent closing by clicking outside
                size="md"
                aria-labelledby="contained-modal-title-vcenter"
                centered
                backdrop="static"
                keyboard={false}
            >
                <Modal.Header style={{ border: "none" }}>
                </Modal.Header>
                <Modal.Body>
                    <Grid size={{ xs: 12 }} container className="mb-3" justifyContent="center">
                        <Grid 
                            size={{ xs: 12 }} 
                            container 
                            flexDirection="column" 
                            alignItems="center" 
                            className="main_sessionmanage"
                        >
                            <img 
                                src={require("../assets/session_timeout.svg").default} 
                                alt='timeout'
                            />
                            <h6 className='text-center'>
                                {isCountdownExpired 
                                    ? "Session Has Expired Due To Inactivity" 
                                    : "The Session Will Expire Soon Due To Inactivity"
                                }
                            </h6>
                            {!isCountdownExpired && (
                                <span className="countdown-timer">
                                    {formatTime(countdown)}
                                </span>
                            )}
                        </Grid>
                        <Grid 
                            size={{ sm: 12, xs: 12 }} 
                            container 
                            justifyContent="flex-end" 
                            className="gap-3 mt-4"
                        >
                            <button 
                                type="button" 
                                className='registersendbtn1 my-0' 
                                onClick={handleClose}
                                disabled={btnLoading}
                            >
                                Exit
                            </button>
                            {!isCountdownExpired && (
                                <button 
                                    type="button" 
                                    onClick={extendSession} 
                                    className='registersendbtn2 my-0'
                                    disabled={btnLoading}
                                >
                                    {btnLoading ? (
                                        <img 
                                            src={require('../assets/whiteloader.gif')} 
                                            alt='loading'
                                        /> 
                                    ) : (
                                        'Continue'
                                    )}
                                </button>
                            )}
                        </Grid>
                    </Grid>
                </Modal.Body>
            </Modal>
        </>
    );
}

export default SessionActive;